import * as THREE from 'three';
import { ProjectilePool } from '../weapons/ProjectilePool';
import { EventBus } from '../core/EventBus';
import { FlyingDrone, spawnDroneSwarm } from './FlyingDrone';

export interface MegaMechConfig {
  position: THREE.Vector3;
  targetRef: THREE.Object3D;
  health?: number;
  damage?: number;
}

const DEFAULT_CONFIG = {
  health: 2000,
  damage: 15,
};

export enum BossPhase {
  PHASE_1 = 1, // Ranged attacks
  PHASE_2 = 2, // Spawns drones + missiles
  PHASE_3 = 3, // Aggressive melee + all attacks
}

let mechIdCounter = 0;

export class MegaMech {
  public readonly id: string;
  public readonly mesh: THREE.Group;

  private scene: THREE.Scene;
  private config: Required<MegaMechConfig>;
  private projectilePool: ProjectilePool;
  private health: number;
  private maxHealth: number;
  private isDestroyed: boolean = false;

  // Phase management
  private currentPhase: BossPhase = BossPhase.PHASE_1;

  // Body parts
  private torso!: THREE.Group;
  private leftArm!: THREE.Group;
  private rightArm!: THREE.Group;
  private head!: THREE.Group;

  // Attack timers
  private primaryFireTimer: number = 0;
  private missileTimer: number = 0;
  private droneSpawnTimer: number = 0;
  private chargeTimer: number = 0;

  // Attack cooldowns per phase
  private readonly PRIMARY_COOLDOWN = [1.0, 0.8, 0.5]; // Faster each phase
  private readonly MISSILE_COOLDOWN = 4.0;
  private readonly DRONE_COOLDOWN = 8.0;

  // Movement
  private isCharging: boolean = false;
  private chargeDirection: THREE.Vector3 = new THREE.Vector3();

  // Spawned minions
  private spawnedDrones: FlyingDrone[] = [];

  // Animation
  private walkCycle: number = 0;
  private armSwing: number = 0;

  constructor(
    scene: THREE.Scene,
    projectilePool: ProjectilePool,
    config: MegaMechConfig
  ) {
    this.id = `megamech_${mechIdCounter++}`;
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...config } as Required<MegaMechConfig>;
    this.projectilePool = projectilePool;
    this.health = this.config.health;
    this.maxHealth = this.config.health;

    // Create mesh
    this.mesh = this.createMechMesh();
    this.mesh.position.copy(config.position);
    scene.add(this.mesh);

    // Set up hit detection
    this.mesh.userData = {
      id: this.id,
      type: 'boss',
      health: this.health,
      hitRadius: 5,
      points: 5000,
      isEnemy: true,
      isBoss: true,
      entity: this,
    };
  }

  private createMechMesh(): THREE.Group {
    const group = new THREE.Group();

    // Create materials
    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0x1a0a2e,
      metalness: 0.9,
      roughness: 0.2,
    });

    const accentMat = new THREE.MeshStandardMaterial({
      color: 0x4a2a6e,
      metalness: 0.8,
      roughness: 0.3,
    });

    const glowMat = new THREE.MeshStandardMaterial({
      color: 0xff00ff,
      emissive: 0xff00ff,
      emissiveIntensity: 1.5,
    });

    // Legs
    this.createLeg(group, -1.5, bodyMat, accentMat);
    this.createLeg(group, 1.5, bodyMat, accentMat);

    // Torso
    this.torso = new THREE.Group();
    this.torso.position.y = 6;

    // Main torso body
    const torsoGeom = new THREE.BoxGeometry(4, 3, 2.5);
    const torsoMesh = new THREE.Mesh(torsoGeom, bodyMat);
    this.torso.add(torsoMesh);

    // Chest plate
    const chestGeom = new THREE.BoxGeometry(3.5, 2, 0.5);
    const chest = new THREE.Mesh(chestGeom, accentMat);
    chest.position.z = 1.3;
    this.torso.add(chest);

    // Core (weak point, glows more as damaged)
    const coreGeom = new THREE.SphereGeometry(0.6, 16, 16);
    const coreMat = new THREE.MeshStandardMaterial({
      color: 0xff0044,
      emissive: 0xff0044,
      emissiveIntensity: 2,
    });
    const core = new THREE.Mesh(coreGeom, coreMat);
    core.position.z = 1.5;
    core.name = 'core';
    this.torso.add(core);

    // Shoulder pads
    const shoulderGeom = new THREE.BoxGeometry(1.5, 1, 1.5);
    const leftShoulder = new THREE.Mesh(shoulderGeom, accentMat);
    leftShoulder.position.set(-2.5, 0.5, 0);
    this.torso.add(leftShoulder);

    const rightShoulder = new THREE.Mesh(shoulderGeom, accentMat);
    rightShoulder.position.set(2.5, 0.5, 0);
    this.torso.add(rightShoulder);

    // Missile pods on shoulders
    this.createMissilePod(this.torso, new THREE.Vector3(-2.5, 1.2, 0), glowMat);
    this.createMissilePod(this.torso, new THREE.Vector3(2.5, 1.2, 0), glowMat);

    group.add(this.torso);

    // Arms
    this.leftArm = this.createArm(-3, bodyMat, accentMat, glowMat);
    this.rightArm = this.createArm(3, bodyMat, accentMat, glowMat);
    this.torso.add(this.leftArm);
    this.torso.add(this.rightArm);

    // Head
    this.head = this.createHead(bodyMat, accentMat, glowMat);
    this.head.position.y = 2;
    this.torso.add(this.head);

    return group;
  }

  private createLeg(parent: THREE.Group, xOffset: number, bodyMat: THREE.Material, accentMat: THREE.Material): void {
    const leg = new THREE.Group();
    leg.position.x = xOffset;

    // Upper leg
    const upperLegGeom = new THREE.BoxGeometry(1.2, 3, 1.2);
    const upperLeg = new THREE.Mesh(upperLegGeom, bodyMat);
    upperLeg.position.y = 4;
    leg.add(upperLeg);

    // Knee joint
    const kneeGeom = new THREE.SphereGeometry(0.7, 8, 8);
    const knee = new THREE.Mesh(kneeGeom, accentMat);
    knee.position.y = 2.5;
    leg.add(knee);

    // Lower leg
    const lowerLegGeom = new THREE.BoxGeometry(1, 2.5, 1);
    const lowerLeg = new THREE.Mesh(lowerLegGeom, bodyMat);
    lowerLeg.position.y = 1.25;
    leg.add(lowerLeg);

    // Foot
    const footGeom = new THREE.BoxGeometry(1.5, 0.5, 2);
    const foot = new THREE.Mesh(footGeom, accentMat);
    foot.position.set(0, 0.25, 0.3);
    leg.add(foot);

    parent.add(leg);
  }

  private createArm(xOffset: number, bodyMat: THREE.Material, accentMat: THREE.Material, glowMat: THREE.Material): THREE.Group {
    const arm = new THREE.Group();
    arm.position.set(xOffset, 0, 0);

    // Upper arm
    const upperArmGeom = new THREE.BoxGeometry(0.8, 2, 0.8);
    const upperArm = new THREE.Mesh(upperArmGeom, bodyMat);
    upperArm.position.y = -1;
    arm.add(upperArm);

    // Elbow
    const elbowGeom = new THREE.SphereGeometry(0.5, 8, 8);
    const elbow = new THREE.Mesh(elbowGeom, accentMat);
    elbow.position.y = -2;
    arm.add(elbow);

    // Forearm with weapon
    const forearmGeom = new THREE.BoxGeometry(0.7, 2, 0.7);
    const forearm = new THREE.Mesh(forearmGeom, bodyMat);
    forearm.position.y = -3;
    arm.add(forearm);

    // Weapon (gatling gun on left, plasma cannon on right)
    if (xOffset < 0) {
      // Gatling gun barrels
      for (let i = 0; i < 4; i++) {
        const angle = (i / 4) * Math.PI * 2;
        const barrelGeom = new THREE.CylinderGeometry(0.08, 0.08, 2, 8);
        const barrel = new THREE.Mesh(barrelGeom, bodyMat);
        barrel.rotation.x = Math.PI / 2;
        barrel.position.set(Math.cos(angle) * 0.2, -4, 1 + Math.sin(angle) * 0.2);
        arm.add(barrel);
      }
      const muzzleGeom = new THREE.CylinderGeometry(0.4, 0.3, 0.3, 8);
      const muzzle = new THREE.Mesh(muzzleGeom, accentMat);
      muzzle.rotation.x = Math.PI / 2;
      muzzle.position.set(0, -4, 2);
      muzzle.name = 'leftMuzzle';
      arm.add(muzzle);
    } else {
      // Plasma cannon
      const cannonGeom = new THREE.CylinderGeometry(0.3, 0.4, 2.5, 8);
      const cannon = new THREE.Mesh(cannonGeom, bodyMat);
      cannon.rotation.x = Math.PI / 2;
      cannon.position.set(0, -4, 1.25);
      arm.add(cannon);

      const plasmaCore = new THREE.Mesh(
        new THREE.SphereGeometry(0.25, 8, 8),
        glowMat
      );
      plasmaCore.position.set(0, -4, 2.5);
      plasmaCore.name = 'rightMuzzle';
      arm.add(plasmaCore);
    }

    return arm;
  }

  private createMissilePod(parent: THREE.Group, position: THREE.Vector3, glowMat: THREE.Material): void {
    const podGroup = new THREE.Group();
    podGroup.position.copy(position);

    const podGeom = new THREE.BoxGeometry(0.8, 0.6, 0.8);
    const podMat = new THREE.MeshStandardMaterial({
      color: 0x2a2a4e,
      metalness: 0.9,
      roughness: 0.2,
    });
    const pod = new THREE.Mesh(podGeom, podMat);
    podGroup.add(pod);

    // Missile tips
    for (let x = -1; x <= 1; x += 2) {
      for (let z = -1; z <= 1; z += 2) {
        const tipGeom = new THREE.ConeGeometry(0.08, 0.2, 6);
        const tip = new THREE.Mesh(tipGeom, glowMat);
        tip.position.set(x * 0.2, 0.4, z * 0.2);
        podGroup.add(tip);
      }
    }

    podGroup.name = position.x < 0 ? 'leftMissilePod' : 'rightMissilePod';
    parent.add(podGroup);
  }

  private createHead(bodyMat: THREE.Material, accentMat: THREE.Material, glowMat: THREE.Material): THREE.Group {
    const head = new THREE.Group();

    // Main head
    const headGeom = new THREE.BoxGeometry(1.5, 1.2, 1.2);
    const headMesh = new THREE.Mesh(headGeom, bodyMat);
    head.add(headMesh);

    // Visor
    const visorGeom = new THREE.BoxGeometry(1.6, 0.4, 0.3);
    const visorMat = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 2,
    });
    const visor = new THREE.Mesh(visorGeom, visorMat);
    visor.position.set(0, 0.2, 0.6);
    visor.name = 'visor';
    head.add(visor);

    // Antenna
    const antennaGeom = new THREE.CylinderGeometry(0.05, 0.05, 0.8, 6);
    const antenna = new THREE.Mesh(antennaGeom, accentMat);
    antenna.position.set(0, 0.9, 0);
    head.add(antenna);

    const antennaTip = new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 8, 8),
      glowMat
    );
    antennaTip.position.set(0, 1.3, 0);
    head.add(antennaTip);

    return head;
  }

  public update(dt: number): void {
    if (this.isDestroyed) return;

    const target = this.config.targetRef;
    if (!target) return;

    // Update phase based on health
    this.updatePhase();

    // Animation
    this.animate(dt);

    // Movement
    this.updateMovement(dt, target.position);

    // Attacks
    this.updateAttacks(dt, target.position);

    // Update spawned drones
    this.updateDrones(dt);
  }

  private updatePhase(): void {
    const healthPercent = this.health / this.maxHealth;

    if (healthPercent <= 0.33 && this.currentPhase !== BossPhase.PHASE_3) {
      this.currentPhase = BossPhase.PHASE_3;
      this.onPhaseChange(BossPhase.PHASE_3);
    } else if (healthPercent <= 0.66 && healthPercent > 0.33 && this.currentPhase !== BossPhase.PHASE_2) {
      this.currentPhase = BossPhase.PHASE_2;
      this.onPhaseChange(BossPhase.PHASE_2);
    }
  }

  private onPhaseChange(phase: BossPhase): void {
    // Visual feedback for phase change
    const core = this.torso.getObjectByName('core') as THREE.Mesh;
    if (core && core.material) {
      const mat = core.material as THREE.MeshStandardMaterial;
      mat.emissiveIntensity = 5;
      setTimeout(() => {
        mat.emissiveIntensity = 2 + (3 - phase);
      }, 500);
    }

    EventBus.emit('boss:phaseChange', {
      id: this.id,
      phase,
    });
  }

  private animate(dt: number): void {
    // Walking animation
    if (!this.isCharging) {
      this.walkCycle += dt * 3;
      const bob = Math.sin(this.walkCycle) * 0.1;
      this.torso.position.y = 6 + bob;
    }

    // Arm swing during movement
    this.armSwing = Math.sin(this.walkCycle) * 0.2;
    this.leftArm.rotation.x = this.armSwing;
    this.rightArm.rotation.x = -this.armSwing;

    // Head tracking
    const target = this.config.targetRef;
    if (target) {
      const toTarget = target.position.clone().sub(this.mesh.position);
      const targetAngle = Math.atan2(toTarget.x, toTarget.z) - this.mesh.rotation.y;
      let angleDiff = targetAngle - this.head.rotation.y;
      while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
      while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
      this.head.rotation.y += angleDiff * dt * 5;
      this.head.rotation.y = Math.max(-0.5, Math.min(0.5, this.head.rotation.y));
    }
  }

  private updateMovement(dt: number, targetPos: THREE.Vector3): void {
    const toTarget = targetPos.clone().sub(this.mesh.position);
    toTarget.y = 0;
    const distance = toTarget.length();

    if (this.isCharging) {
      // Charge attack movement
      this.mesh.position.addScaledVector(this.chargeDirection, 25 * dt);
      this.chargeTimer -= dt;
      if (this.chargeTimer <= 0) {
        this.isCharging = false;
      }
      return;
    }

    // Normal movement - maintain distance
    const preferredDist = this.currentPhase === BossPhase.PHASE_3 ? 10 : 20;
    let moveSpeed = 4 + this.currentPhase; // Faster each phase

    if (distance > preferredDist + 5) {
      // Move closer
      const moveDir = toTarget.normalize();
      this.mesh.position.addScaledVector(moveDir, moveSpeed * dt);
    } else if (distance < preferredDist - 5) {
      // Back away
      const moveDir = toTarget.normalize();
      this.mesh.position.addScaledVector(moveDir, -moveSpeed * 0.5 * dt);
    }

    // Rotate to face target
    const targetAngle = Math.atan2(toTarget.x, toTarget.z);
    let angleDiff = targetAngle - this.mesh.rotation.y;
    while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
    this.mesh.rotation.y += angleDiff * dt * 2;
  }

  private updateAttacks(dt: number, targetPos: THREE.Vector3): void {
    const distance = this.mesh.position.distanceTo(targetPos);

    // Primary fire (both arms)
    this.primaryFireTimer -= dt;
    if (this.primaryFireTimer <= 0 && distance < 40) {
      this.firePrimary(targetPos);
      this.primaryFireTimer = this.PRIMARY_COOLDOWN[this.currentPhase - 1];
    }

    // Missile attacks (Phase 2+)
    if (this.currentPhase >= BossPhase.PHASE_2) {
      this.missileTimer -= dt;
      if (this.missileTimer <= 0 && distance < 50) {
        this.fireMissiles(targetPos);
        this.missileTimer = this.MISSILE_COOLDOWN;
      }

      // Drone spawning
      this.droneSpawnTimer -= dt;
      if (this.droneSpawnTimer <= 0) {
        this.spawnDrones();
        this.droneSpawnTimer = this.DRONE_COOLDOWN;
      }
    }

    // Charge attack (Phase 3 only)
    if (this.currentPhase === BossPhase.PHASE_3 && !this.isCharging) {
      if (distance < 25 && distance > 8 && Math.random() < 0.01) {
        this.startCharge(targetPos);
      }
    }
  }

  private firePrimary(targetPos: THREE.Vector3): void {
    // Fire from both arm weapons
    const leftMuzzlePos = new THREE.Vector3(-3, 6 - 4, 2);
    leftMuzzlePos.applyQuaternion(this.mesh.quaternion);
    leftMuzzlePos.add(this.mesh.position);

    const rightMuzzlePos = new THREE.Vector3(3, 6 - 4, 2.5);
    rightMuzzlePos.applyQuaternion(this.mesh.quaternion);
    rightMuzzlePos.add(this.mesh.position);

    // Left arm - rapid fire
    const leftDir = targetPos.clone().sub(leftMuzzlePos).normalize();
    leftDir.x += (Math.random() - 0.5) * 0.1;
    leftDir.y += (Math.random() - 0.5) * 0.05;
    leftDir.z += (Math.random() - 0.5) * 0.1;
    leftDir.normalize();

    this.projectilePool.spawn(
      leftMuzzlePos,
      leftDir,
      60,
      this.config.damage * 0.5,
      new THREE.Color(0xff8800)
    );

    // Right arm - plasma bolt
    const rightDir = targetPos.clone().sub(rightMuzzlePos).normalize();
    this.projectilePool.spawn(
      rightMuzzlePos,
      rightDir,
      45,
      this.config.damage,
      new THREE.Color(0xff00ff)
    );
  }

  private fireMissiles(_targetPos: THREE.Vector3): void {
    // Fire missiles from shoulder pods
    const podPositions = [
      new THREE.Vector3(-2.5, 7.2, 0),
      new THREE.Vector3(2.5, 7.2, 0),
    ];

    for (const localPos of podPositions) {
      const worldPos = localPos.clone();
      worldPos.applyQuaternion(this.mesh.quaternion);
      worldPos.add(this.mesh.position);

      // Missiles arc up then down
      const direction = new THREE.Vector3(0, 1, 0);

      // Spawn multiple missiles per pod
      for (let i = 0; i < 2; i++) {
        const offset = new THREE.Vector3(
          (Math.random() - 0.5) * 0.5,
          0,
          (Math.random() - 0.5) * 0.5
        );

        this.projectilePool.spawn(
          worldPos.clone().add(offset),
          direction.clone().add(offset.normalize().multiplyScalar(0.3)).normalize(),
          25,
          this.config.damage * 0.8,
          new THREE.Color(0xff4400)
        );
      }
    }
  }

  private spawnDrones(): void {
    if (this.spawnedDrones.length >= 6) return; // Max drones

    const swarm = spawnDroneSwarm(
      this.scene,
      this.projectilePool,
      this.mesh.position.clone().add(new THREE.Vector3(0, 5, 0)),
      this.config.targetRef,
      2
    );

    for (const drone of swarm) {
      this.spawnedDrones.push(drone);
      // Notify Game to register drone for collision detection
      EventBus.emit('boss:droneSpawned', { drone });
    }
  }

  private updateDrones(dt: number): void {
    for (const drone of this.spawnedDrones) {
      if (drone.isAlive()) {
        drone.update(dt);
      }
    }
    this.spawnedDrones = this.spawnedDrones.filter(d => d.isAlive());
  }

  private startCharge(targetPos: THREE.Vector3): void {
    this.isCharging = true;
    this.chargeTimer = 1.5;
    this.chargeDirection = targetPos.clone().sub(this.mesh.position);
    this.chargeDirection.y = 0;
    this.chargeDirection.normalize();
  }

  public takeDamage(damage: number, hitPosition?: THREE.Vector3): void {
    if (this.isDestroyed) return;

    let actualDamage = damage;

    // Check for core hit (center mass, deals extra damage)
    if (hitPosition) {
      const corePos = this.mesh.position.clone().add(new THREE.Vector3(0, 6, 1.5));
      const toCoreDistance = hitPosition.distanceTo(corePos);
      if (toCoreDistance < 1.5) {
        actualDamage *= 2;
        this.flashCore();
      }
    }

    this.health -= actualDamage;
    this.mesh.userData.health = this.health;

    // Emit damage event
    EventBus.emit('target:damaged', {
      id: this.id,
      damage: actualDamage,
      healthRemaining: this.health,
      isBoss: true,
    });

    // Flash effect
    this.flashDamage();

    if (this.health <= 0) {
      this.destroy();
    }
  }

  private flashCore(): void {
    const core = this.torso.getObjectByName('core') as THREE.Mesh;
    if (core && core.material) {
      const mat = core.material as THREE.MeshStandardMaterial;
      mat.emissiveIntensity = 8;
      setTimeout(() => {
        mat.emissiveIntensity = 2 + (3 - this.currentPhase);
      }, 150);
    }
  }

  private flashDamage(): void {
    const visor = this.head.getObjectByName('visor') as THREE.Mesh;
    if (visor && visor.material) {
      const mat = visor.material as THREE.MeshStandardMaterial;
      const original = mat.emissiveIntensity;
      mat.emissiveIntensity = 5;
      setTimeout(() => {
        mat.emissiveIntensity = original;
      }, 100);
    }
  }

  private destroy(): void {
    this.isDestroyed = true;

    // Destroy spawned drones
    for (const drone of this.spawnedDrones) {
      if (drone.isAlive()) {
        drone.takeDamage(1000); // Force destroy
      }
    }

    // Emit destroyed event
    EventBus.emit('target:destroyed', {
      id: this.id,
      position: {
        x: this.mesh.position.x,
        y: this.mesh.position.y,
        z: this.mesh.position.z,
      },
      points: this.mesh.userData.points,
      isBoss: true,
    });

    EventBus.emit('boss:defeated', {
      id: this.id,
    });

    // Remove mesh
    if (this.mesh.parent) {
      this.mesh.parent.remove(this.mesh);
    }
  }

  public isAlive(): boolean {
    return !this.isDestroyed;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public getMesh(): THREE.Group {
    return this.mesh;
  }

  public getPhase(): BossPhase {
    return this.currentPhase;
  }

  public getHealthPercent(): number {
    return this.health / this.maxHealth;
  }

  public getSpawnedDrones(): FlyingDrone[] {
    return this.spawnedDrones;
  }
}
